// project import
import Loader from 'components/Loader';
import useAuth from 'hooks/useAuth';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import MainLayout from 'layout/MainLayout';
import { CreateRoutePage } from 'components/create-dialog/CreateRoutePage';
import AdminHome from 'pages/admin/AdminHome';
import { isAdminVisible } from 'pages/admin/adminItems';
import { FirmWidgetsPage } from 'pages/admin/FirmWidgetsPage';
import RouteErrorPage from 'pages/error/RouteErrorPage';
import JobPositionPage from 'pages/job-positions/JobPositionPage';
import JobPositionPageEdit from 'pages/job-positions/JobPositionPageEdit';
import NDFExportPage from 'pages/ndf/NDFExportPage.tsx';
import ForeignSalesForm from 'pages/reports/ForeignSalesForm.tsx';
import OpenAdvancesForm from 'pages/reports/OpenAdvancesForm.tsx';
import OpenInvoicesForm from 'pages/reports/OpenInvoicesForm.tsx';
import SalesJournalForm from 'pages/reports/SalesJournalForm.tsx';
import UnpaidInvoicesForm from 'pages/reports/UnpaidInvoicesForm.tsx';
import { ReportRoute } from 'pages/reporting/ReportRoute';
import { ReportsHubPage } from 'pages/reporting/ReportsHubPage';
import Settings from 'pages/settings/Settings.tsx';
import { useMediaQuery } from '@uidotdev/usehooks';
import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { AI_CHAT_MOBILE_QUERY } from 'types/aiChat';
import { INVOICE_DETAIL_ROUTE_PATHS, MATTER_EDIT_ROUTE_PATHS } from 'routes/constants';
import TourProvider from 'tour/TourProvider';
import { FeatureFlag } from 'types/features';
import { SHARE_WITH_FIRM_PERMISSIONS } from 'types/homeWidgetSharing';
import { useHasPermissions } from 'utils/permissionUtil';
import AuthGuard from 'utils/route-guard/AuthGuard';
import { NumericIdGuard } from 'utils/route-guard/NumericIdGuard.tsx';
import { useFeatureFlags } from 'utils/useFeatureFlags';
import { CONTACT_MERGE_PATH, contactMergeRouteElement } from './contactMergeRouteEntry.tsx';
import { MEETING_PREP_VIEW_PATH, meetingPrepViewRouteElement } from './meetingPrepViewRouteEntry.tsx';
import { ProtectedRoute } from './ProtectedRoute.tsx';

// page imports
import { AuditTrailListPage } from 'pages/audit-trail/AuditTrailListPage';
import BankTransferDetails from 'pages/bank-transfers/BankTransferDetails';
import BankTransfersPage from 'pages/bank-transfers/BankTransfersPage';
import BillableMattersPage from 'pages/billable-clients/BillableMattersPage';
import CoiCheckPage from 'pages/coi/CoiCheckPage';
import ConflictCheckReports from 'pages/coi/ConflictCheckReports';
import CompliancePage from 'pages/compliance/CompliancePage';
import ContactPage from 'pages/contacts/ContactPage';
import ContactPageEdit from 'pages/contacts/ContactPageEdit';
import ContactsPage from 'pages/contacts/ContactsPage';
import ContractPage from 'pages/contracts/ContractPage';
import ContractPageEdit from 'pages/contracts/ContractPageEdit';
import ContractsListPage from 'pages/contracts/ContractsListPage';
import CorrespondenceListPage from 'pages/correspondence/CorrespondenceListPage';
import CorrespondencePage from 'pages/correspondence/CorrespondencePage';
import CorrespondencePageEdit from 'pages/correspondence/CorrespondencePageEdit';
import CountryManagementPage from 'pages/country-management/CountryManagementPage';
import EffortPage from 'pages/efforts/EffortPage';
import EffortPageEdit from 'pages/efforts/EffortPageEdit';
import EffortsPage from 'pages/efforts/EffortsPage';
import EmployeePage from 'pages/employees/EmployeePage';
import EmployeePageEdit from 'pages/employees/EmployeePageEdit';
import EmployeesPage from 'pages/employees/EmployeesPage';
import NotFoundPage from 'pages/error/NotFoundPage';
import ExpensePage from 'pages/expenses/ExpensePage';
import ExpensePageEdit from 'pages/expenses/ExpensePageEdit';
import ExpensesPage from 'pages/expenses/ExpensesPage';
import ExperienceRecordListPage from 'pages/experience/ExperienceRecordListPage.tsx';
import ExperienceRecordPage from 'pages/experience/ExperienceRecordPage.tsx';
import ExperienceRecordPageEdit from 'pages/experience/ExperienceRecordPageEdit.tsx';
import FinancialConditionsPage from 'pages/financial-conditions/FinancialConditionsPage';
import GroupPage from 'pages/groups/GroupPage';
import GroupPageEdit from 'pages/groups/GroupPageEdit';
import GroupsPage from 'pages/groups/GroupsPage';
import HomeRoute from 'pages/home/HomeRoute';
import InvoicePage from 'pages/invoices/InvoicePage';
import InvoicesPage from 'pages/invoices/InvoicesPage';
import JobPositionsListPage from 'pages/job-positions/JobPositionsListPage';
import JobRolePage from 'pages/job-roles/JobRolePage';
import JobRolePageEdit from 'pages/job-roles/JobRolePageEdit';
import JobRolesListPage from 'pages/job-roles/JobRolesListPage';
import LawFirmsPage from 'pages/law-firms/LawFirmsPage';
import MarketingProfileDetailsPage from 'pages/marketing-profiles/MarketingProfileDetailsPage';
import MarketingProfilesPage from 'pages/marketing-profiles/MarketingProfilesPage';
import MatterPage from 'pages/matters/MatterPage';
import MatterPageEdit from 'pages/matters/MatterPageEdit';
import MattersListPage from 'pages/matters/MattersListPage';
import MyPortfolioPage from 'pages/my-portfolio/MyPortfolioPage';
import OfficePage from 'pages/offices/OfficePage';
import OfficePageEdit from 'pages/offices/OfficePageEdit';
import OfficestPage from 'pages/offices/OfficesPage';
import PracticeAreaPage from 'pages/practice-areas/PracticeAreaPage';
import PracticeAreaPageEdit from 'pages/practice-areas/PracticeAreaPageEdit';
import PracticeAreasListPage from 'pages/practice-areas/PracticeAreasListPage';
import PrepaymentsPage from 'pages/prepayments/PrepaymentsPage';
import PricelistsPage from 'pages/pricelists/PricelistsPage';
import ProjectPage from 'pages/projects/ProjectPage.tsx';
import ProjectPageEdit from 'pages/projects/ProjectPageEdit.tsx';
import ProjectsListPage from 'pages/projects/ProjectsListPage.tsx';
import { SearchResultsPage } from 'pages/search/SearchResultsPage';
import TaskPage from 'pages/tasks/TaskPage';
import TaskPageEdit from 'pages/tasks/TaskPageEdit';
import TasksPage from 'pages/tasks/TasksPage';
import TeamPage from 'pages/teams/TeamPage';
import TeamPageEdit from 'pages/teams/TeamPageEdit';
import TeamsListPage from 'pages/teams/TeamsListPage';
import FinishTimecardPageForm from 'pages/timecards/FinishTimecardPageForm';
import TimecardEditPage from 'pages/timecards/TimecardEditPage';
import TimecardListPage from 'pages/timecards/TimecardListPage';
import TimecardPage from 'pages/timecards/TimecardPage';
// Both AI chat surfaces are heavy (mermaid, shiki, streamdown, cytoscape, katex, …)
// and share those deps. Lazy-loading them splits the deps into an async chunk
// loaded only when the AI chat route is visited, and avoids cross-chunk circular
// imports.
const AiChatPage = lazy(() => import('pages/ai-chat/AiChatPage'));
const MobileAiChatScreen = lazy(() =>
    import('pages/ai-chat/mobile/MobileAiChatScreen').then((module) => ({ default: module.MobileAiChatScreen }))
);
const HelpCenterPage = lazy(() =>
    import('pages/help-center/HelpCenterPage').then((module) => ({ default: module.HelpCenterPage }))
);
const helpCenterElement = (
    <Suspense fallback={<Loader />}>
        <HelpCenterPage />
    </Suspense>
);

// `@__PURE__` so that when the dev-only route below compiles away, Rollup may drop this call
// too — without it the call is assumed to have side effects, the arrow keeps the dynamic import
// alive, and the gallery chunk ships to production despite being unreachable.
const KitGalleryPage = /* @__PURE__ */ lazy(() =>
    import('pages/kit-gallery/KitGalleryPage').then((module) => ({ default: module.KitGalleryPage }))
);

const DelegationsPage = lazy(() => import('pages/delegations/DelegationsPage'));
const DelegationWizardPage = lazy(() => import('pages/delegations/DelegationWizardPage'));
const DelegationDetailPage = lazy(() => import('pages/delegations/DelegationDetailPage'));
const DelegationEditCasesPage = lazy(() => import('pages/delegations/DelegationEditCasesPage'));

const ClientAssetsListPage = lazy(() => import('pages/client-assets/ClientAssetsListPage'));
const ClientAssetPage = lazy(() => import('pages/client-assets/ClientAssetPage'));
const ClientAssetPageEdit = lazy(() => import('pages/client-assets/ClientAssetPageEdit'));

const DebriefingsListPage = lazy(() => import('pages/debriefing/DebriefingsListPage'));
const DebriefingPage = lazy(() => import('pages/debriefing/DebriefingPage'));
const DebriefingPageEdit = lazy(() => import('pages/debriefing/DebriefingPageEdit'));

const TrainingsListPage = lazy(() => import('pages/trainings/TrainingsListPage'));
const RankingsRefereesPage = lazy(() => import('pages/rankings-referees/RankingsRefereesPage'));
const TrainingsReportsPage = lazy(() => import('pages/trainings/TrainingsReportsPage'));
const TrainingReadPage = lazy(() => import('pages/trainings/TrainingReadPage'));
const TrainingPageEdit = lazy(() => import('pages/trainings/TrainingPageEdit'));

const BREADCRUMB_COLLECTION = {
    bankTransfers: [{ path: '/bank-transfers', title: 'bank-transfers.bank-transfers' }],
    contacts: [{ path: '/contacts', title: 'contacts.contacts' }],
    marketingProfiles: [
        { path: '/contacts', title: 'contacts.contacts' },
        { path: '/contacts/marketing', title: 'marketing-profiles.profiles' },
    ],
    marketingProfileDetails: [
        { path: '/contacts', title: 'contacts.contacts' },
        { path: '/contacts/marketing', title: 'marketing-profiles.profiles' },
    ],
    correspondence: [{ path: '/correspondence', title: 'correspondence.correspondence' }],
    debriefings: [{ path: '/debriefings', title: 'debriefing.debriefings' }],
    employees: [{ path: '/employees', title: 'employees.colleagues' }],
    efforts: [{ path: '/efforts', title: 'efforts.efforts' }],
    rankingsReferees: [{ path: '/rankings-referees', title: 'rankings-referees.title' }],
    expenses: [{ path: '/expenses', title: 'expenses.expenses' }],
    experienceRecords: [{ path: '/experience-records', title: 'experience-records.experience-records' }],
    lawFirms: [{ path: '/law-firms', title: 'law-firms.law-firms-hub' }],
    groups: [{ path: '/groups', title: 'groups.groups' }],
    contracts: [{ path: '/contracts', title: 'contracts.contracts' }],
    clientAssets: [{ path: '/client-assets', title: 'menu.client-assets' }],
    matters: [{ path: '/matters', title: 'matters.matters' }],
    projects: [{ path: '/projects', title: 'projects.projects' }],
    delegations: [{ path: '/delegations', title: 'menu.delegations' }],
    offices: [{ path: '/offices', title: 'offices.offices' }],
    practiceAreas: [{ path: '/practice-areas', title: 'practice-areas.practice-areas' }],
    teams: [{ path: '/teams', title: 'teams.teams' }],
    invoices: [{ path: '/invoices', title: 'invoices.invoices' }],
    prepayments: [{ path: '/prepayments', title: 'prepayments.prepayments' }],
    jobRoles: [{ path: '/job-roles', title: 'job-roles.job-roles' }],
    jobPositions: [{ path: '/job-positions', title: 'job-positions.job-positions' }],
    financialConditions: [{ path: '/financial-conditions', title: 'financial-conditions.financial-conditions' }],
    timecards: [{ path: '/timecards', title: 'timecards.timecards' }],
    coiChecks: [{ path: '/coi-checks', title: 'coi.coi-check' }],
    aiChat: [{ path: '/ai-chat', title: 'ai-chat.title' }],
    helpCenter: [{ path: '/help-center', title: 'help-center.title' }],
    myPortfolio: [{ path: '/my-portfolio', title: 'portfolio.my-portfolio' }],
    billableClients: [{ path: '/billable-clients', title: 'menu.billable-clients' }],
    compliance: [{ path: '/compliance', title: 'compliance.compliance' }],
    searchResults: [{ path: '/search', title: 'global-search.results-label' }],
    settings: [{ path: '/settings', title: 'menu.settings' }],
    tasks: [{ path: '/tasks', title: 'tasks.tasks' }],
    pricelists: [{ path: '/pricelists', title: 'pricelists.pricelists' }],
    countryManagement: [{ path: '/country-management', title: 'menu.country-management' }],
    auditTrail: [{ path: '/audit-trail', title: 'audit-trail.title' }],
    ndfHbr: [{ path: '/ndf-export-hbr', title: 'menu.payroll-hbr' }],
    ndfErp: [{ path: '/ndf-export-erp', title: 'menu.sunsystems-erp' }],
    openAdvances: [{ path: '/reports-open-advances', title: 'reports.open-advances.title' }],
    openInvoices: [{ path: '/reports-open-invoices', title: 'menu.open-invoices' }],
    unpaidInvoices: [{ path: '/reports-unpaid-invoices', title: 'menu.unpaid-invoices' }],
    salesJournal: [{ path: '/reports-sales-journal', title: 'reports.sales-journal.title' }],
    foreignSales: [{ path: '/reports-foreign-sales', title: 'reports.foreign-sales.title' }],
    trainings: [{ path: '/trainings', title: 'trainings.title' }],
    trainingsReports: [{ path: '/trainings/reports', title: 'trainings.reports.title' }],
    reports: [{ path: '/reports', title: 'reporting.title' }],
};

const ADMIN_BREADCRUMB = { path: '/admin', title: 'menu.admin' };

const AiChatRoute = (): JSX.Element => {
    const { isFlowEnabled } = useFeatureFlags(FeatureFlag.AI_CHAT_ENABLED);
    const hasPermission = useHasPermissions(['READ.CRESPECT_AI']);
    // On a phone the assistant is the whole screen instead of a page inside the portal chrome,
    // which stays desktop-only.
    const isMobile = useMediaQuery(AI_CHAT_MOBILE_QUERY);

    if (!isFlowEnabled || !hasPermission) {
        return <Navigate to="/" replace />;
    }

    return <Suspense fallback={<Loader />}>{isMobile ? <MobileAiChatScreen /> : <AiChatPage />}</Suspense>;
};

const CorrespondenceRoute = ({ children }: { children: JSX.Element }): JSX.Element => {
    const { isFlowEnabled } = useFeatureFlags(FeatureFlag.CORRESPONDENCE_MODULE);
    const hasPermission = useHasPermissions([
        'READ.CORRESPONDENCE.ALL',
        'READ.CORRESPONDENCE.MY_CASES',
        'READ.CORRESPONDENCE.CREATED_BY_ME',
        'READ.CORRESPONDENCE.MY_OWN',
    ]);

    if (!isFlowEnabled || !hasPermission) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// `/admin`: in the new design the Admin home, for anyone with an Admin page to open; in classic the
// Settings page, as `/settings`.
const AdminRoute = (): JSX.Element => {
    const isNew = useIsNewDesign();
    const { user } = useAuth();

    if (!isNew) {
        return <ProtectedRoute redirectPath="/" element={<Settings />} permissions={['READ.BACK_OFFICE_SETTINGS']} />;
    }

    return isAdminVisible(user) ? <AdminHome /> : <Navigate to="/" replace />;
};
// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
    path: '/',
    children: [
        {
            path: '/',
            element: (
                <AuthGuard>
                    <TourProvider>
                        <MainLayout />
                    </TourProvider>
                </AuthGuard>
            ),
            children: [
                {
                    errorElement: <RouteErrorPage />,
                    children: [
                        {
                            path: '/404',
                            element: <NotFoundPage />,
                        },
                        // The component kit rendered as one page, so the set is reviewed at once
                        // rather than screen by screen. Development builds only: `import.meta.env.DEV`
                        // is a compile-time constant, so a production build drops this route and the
                        // lazy chunk behind it along with the reference. Also absent from the
                        // navigation and the search index, so nothing links to it even in dev.
                        ...(import.meta.env.DEV
                            ? [
                                  {
                                      path: '/kit-gallery',
                                      element: (
                                          <Suspense fallback={<Loader />}>
                                              <KitGalleryPage />
                                          </Suspense>
                                      ),
                                  },
                              ]
                            : []),
                        {
                            path: '/billable-clients',
                            element: <BillableMattersPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['billableClients'],
                            searchableName: 'menu.billable-clients',
                        },
                        {
                            path: '/bank-transfers',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<BankTransfersPage />}
                                    permissions={['READ.BANK_TRANSACTIONS.ALL']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['bankTransfers'],
                            searchableName: 'menu.bank-transfers',
                            permissions: ['READ.BANK_TRANSACTIONS.ALL'],
                        },
                        {
                            path: '/bank-transfers/:id',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard>
                                            <BankTransferDetails />
                                        </NumericIdGuard>
                                    }
                                    permissions={['READ.BANK_TRANSACTIONS.ALL']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['bankTransfers'],
                            searchableName: 'command-palette.bank-transfer-details',
                            permissions: ['READ.BANK_TRANSACTIONS.ALL'],
                        },
                        {
                            path: '/compliance',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<CompliancePage />}
                                    permissions={[
                                        'READ.PARTIES.ALL',
                                        'READ.PARTIES.MY_CONTACTS',
                                        'READ.PARTIES.RELATED_TO_MY_CASES',
                                    ]}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['compliance'],
                            searchableName: 'compliance.compliance',
                            permissions: [
                                'READ.PARTIES.ALL',
                                'READ.PARTIES.MY_CONTACTS',
                                'READ.PARTIES.RELATED_TO_MY_CASES',
                            ],
                        },
                        {
                            path: 'home',
                            element: <HomeRoute />,
                            searchableName: 'menu.dashboard',
                        },
                        {
                            // No `searchableName`: the results page would otherwise list itself
                            // among the pages a query matches.
                            path: 'search',
                            element: <SearchResultsPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['searchResults'],
                        },
                        {
                            path: 'ai-chat',
                            element: <AiChatRoute />,
                            breadcrumbs: BREADCRUMB_COLLECTION['aiChat'],
                            searchableName: 'ai-chat.title',
                            permissions: ['READ.CRESPECT_AI'],
                        },
                        {
                            path: MEETING_PREP_VIEW_PATH,
                            element: meetingPrepViewRouteElement,
                            breadcrumbs: BREADCRUMB_COLLECTION['aiChat'],
                            permissions: ['READ.CRESPECT_AI'],
                        },
                        {
                            path: 'help-center',
                            element: helpCenterElement,
                            breadcrumbs: BREADCRUMB_COLLECTION['helpCenter'],
                            searchableName: 'help-center.title',
                        },
                        {
                            path: 'help-center/:pageId',
                            element: helpCenterElement,
                            breadcrumbs: BREADCRUMB_COLLECTION['helpCenter'],
                        },
                        {
                            path: 'contacts',
                            element: <ContactsPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['contacts'],
                            searchableName: 'contacts.contacts',
                            permissions: [
                                'READ.PARTIES.ALL',
                                'READ.PARTIES.MY_CONTACTS',
                                'READ.PARTIES.RELATED_TO_MY_CASES',
                            ],
                        },
                        {
                            path: 'contacts/marketing/new',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<CreateRoutePage />}
                                    permissions={['CREATE.MARKETING_PROFILE']}
                                    requiredFeature={FeatureFlag.MARKETING_PROFILE_PAGE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['marketingProfileDetails'],
                            permissions: ['CREATE.MARKETING_PROFILE'],
                            requiredFeature: FeatureFlag.MARKETING_PROFILE_PAGE,
                        },
                        {
                            path: 'contacts/marketing/:id',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard>
                                            <MarketingProfileDetailsPage />
                                        </NumericIdGuard>
                                    }
                                    permissions={['READ.MARKETING.ALL']}
                                    requiredFeature={FeatureFlag.MARKETING_PROFILE_PAGE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['marketingProfileDetails'],
                            permissions: ['READ.MARKETING.ALL'],
                            requiredFeature: FeatureFlag.MARKETING_PROFILE_PAGE,
                        },
                        {
                            path: 'contacts/marketing/duplicate/:id',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard>
                                            <CreateRoutePage />
                                        </NumericIdGuard>
                                    }
                                    permissions={['READ.MARKETING.ALL', 'CREATE.MARKETING_PROFILE']}
                                    requiredFeature={FeatureFlag.MARKETING_PROFILE_PAGE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['marketingProfileDetails'],
                            permissions: ['READ.MARKETING.ALL', 'CREATE.MARKETING_PROFILE'],
                            requiredFeature: FeatureFlag.MARKETING_PROFILE_PAGE,
                        },
                        {
                            path: 'contacts/marketing',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<MarketingProfilesPage />}
                                    permissions={['READ.MARKETING.ALL']}
                                    requiredFeature={FeatureFlag.MARKETING_PROFILE_PAGE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['marketingProfiles'],
                            searchableName: 'marketing-profiles.profiles',
                            permissions: ['READ.MARKETING.ALL'],
                            requiredFeature: FeatureFlag.MARKETING_PROFILE_PAGE,
                        },
                        {
                            path: '/contact/:id',
                            element: (
                                <NumericIdGuard>
                                    <ContactPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['contacts'],
                            searchableName: 'command-palette.contact-details',
                            permissions: [
                                'READ.PARTIES.ALL',
                                'READ.PARTIES.MY_CONTACTS',
                                'READ.PARTIES.RELATED_TO_MY_CASES',
                            ],
                        },
                        {
                            path: '/contact/:id/:tab',
                            element: (
                                <NumericIdGuard>
                                    <ContactPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['contacts'],
                        },
                        {
                            path: '/contact/:id/invoices/:invoiceTab',
                            element: (
                                <NumericIdGuard>
                                    <ContactPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['contacts'],
                        },
                        {
                            path: '/contact/new',
                            focusedEdit: 'contacts',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['contacts'],
                        },
                        {
                            path: '/contact/:id/edit',
                            focusedEdit: 'contacts',
                            element: (
                                <NumericIdGuard>
                                    <ContactPageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['contacts'],
                            searchableName: 'command-palette.contact-edit',
                            permissions: [
                                'READ.PARTIES.ALL',
                                'READ.PARTIES.MY_CONTACTS',
                                'READ.PARTIES.RELATED_TO_MY_CASES',
                            ],
                        },
                        {
                            path: '/correspondence',
                            element: (
                                <CorrespondenceRoute>
                                    <CorrespondenceListPage />
                                </CorrespondenceRoute>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['correspondence'],
                            searchableName: 'menu.correspondence',
                            permissions: [
                                'READ.CORRESPONDENCE.ALL',
                                'READ.CORRESPONDENCE.MY_CASES',
                                'READ.CORRESPONDENCE.CREATED_BY_ME',
                                'READ.CORRESPONDENCE.MY_OWN',
                            ],
                            requiredFeature: FeatureFlag.CORRESPONDENCE_MODULE,
                        },
                        {
                            path: '/correspondence/new',
                            focusedEdit: 'correspondence',
                            element: (
                                <CorrespondenceRoute>
                                    <CreateRoutePage />
                                </CorrespondenceRoute>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['correspondence'],
                        },
                        {
                            path: '/correspondence/:id',
                            element: (
                                <CorrespondenceRoute>
                                    <NumericIdGuard>
                                        <CorrespondencePage />
                                    </NumericIdGuard>
                                </CorrespondenceRoute>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['correspondence'],
                            searchableName: 'command-palette.correspondence-details',
                            permissions: [
                                'READ.CORRESPONDENCE.ALL',
                                'READ.CORRESPONDENCE.MY_CASES',
                                'READ.CORRESPONDENCE.CREATED_BY_ME',
                                'READ.CORRESPONDENCE.MY_OWN',
                            ],
                            requiredFeature: FeatureFlag.CORRESPONDENCE_MODULE,
                        },
                        {
                            path: '/correspondence/:id/:tab',
                            element: (
                                <CorrespondenceRoute>
                                    <NumericIdGuard>
                                        <CorrespondencePage />
                                    </NumericIdGuard>
                                </CorrespondenceRoute>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['correspondence'],
                        },
                        {
                            path: '/correspondence/:id/edit',
                            focusedEdit: 'correspondence',
                            element: (
                                <CorrespondenceRoute>
                                    <NumericIdGuard>
                                        <CorrespondencePageEdit />
                                    </NumericIdGuard>
                                </CorrespondenceRoute>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['correspondence'],
                        },
                        {
                            path: '/correspondence/:correspondenceId/task/new',
                            focusedEdit: 'tasks',
                            element: (
                                <CorrespondenceRoute>
                                    <NumericIdGuard paramName="correspondenceId">
                                        <CreateRoutePage />
                                    </NumericIdGuard>
                                </CorrespondenceRoute>
                            ),
                        },
                        {
                            path: '/contact/:contactId/matter/new',
                            focusedEdit: 'matters',
                            element: (
                                <NumericIdGuard paramName="contactId">
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['contacts'],
                        },
                        {
                            path: '/contact/:contactId/contract/new',
                            focusedEdit: 'contracts',
                            element: (
                                <NumericIdGuard paramName="contactId">
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['contacts'],
                        },
                        {
                            path: '/contact/:contactId/task/new',
                            focusedEdit: 'tasks',
                            element: (
                                <NumericIdGuard paramName="contactId">
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                        },
                        {
                            path: INVOICE_DETAIL_ROUTE_PATHS.CONTACT,
                            element: (
                                <NumericIdGuard>
                                    <InvoicePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['invoices'],
                        },
                        {
                            path: INVOICE_DETAIL_ROUTE_PATHS.CONTACT_TAB,
                            element: (
                                <NumericIdGuard>
                                    <InvoicePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['invoices'],
                        },
                        {
                            path: INVOICE_DETAIL_ROUTE_PATHS.MATTER,
                            element: (
                                <NumericIdGuard>
                                    <InvoicePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['invoices'],
                        },
                        {
                            path: INVOICE_DETAIL_ROUTE_PATHS.MATTER_TAB,
                            element: (
                                <NumericIdGuard>
                                    <InvoicePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['invoices'],
                        },
                        {
                            path: '/matter/:matterId/task/new',
                            focusedEdit: 'tasks',
                            element: (
                                <NumericIdGuard paramName="matterId">
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                        },
                        {
                            path: '/matter/:matterId/effort/new',
                            focusedEdit: 'efforts',
                            element: (
                                <NumericIdGuard paramName="matterId">
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['matters'],
                        },
                        {
                            path: 'contracts',
                            element: <ContractsListPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['contracts'],
                            searchableName: 'contracts.contracts',
                            permissions: ['READ.CONTRACTS.PARTY_ACCESS'],
                        },
                        {
                            path: '/contract/:id',
                            element: (
                                <NumericIdGuard>
                                    <ContractPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['contracts'],
                            searchableName: 'command-palette.contract-details',
                            permissions: ['READ.CONTRACTS.PARTY_ACCESS'],
                        },
                        {
                            path: '/contract/new',
                            focusedEdit: 'contracts',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['contracts'],
                        },
                        {
                            path: '/contract/:id/edit',
                            focusedEdit: 'contracts',
                            element: (
                                <NumericIdGuard>
                                    <ContractPageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['contracts'],
                        },
                        {
                            path: '/client-assets',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <Suspense fallback={<Loader />}>
                                            <ClientAssetsListPage />
                                        </Suspense>
                                    }
                                    permissions={['READ.PARTY_ASSETS']}
                                    requiredFeature={FeatureFlag.PARTY_ASSETS}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['clientAssets'],
                            searchableName: 'menu.client-assets',
                            permissions: ['READ.PARTY_ASSETS'],
                            requiredFeature: FeatureFlag.PARTY_ASSETS,
                        },
                        {
                            path: '/client-asset/new',
                            focusedEdit: 'client-assets',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<CreateRoutePage />}
                                    permissions={['CREATE.PARTY_ASSETS']}
                                    requiredFeature={FeatureFlag.PARTY_ASSETS}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['clientAssets'],
                            permissions: ['CREATE.PARTY_ASSETS'],
                            requiredFeature: FeatureFlag.PARTY_ASSETS,
                        },
                        {
                            path: '/matter/:matterId/client-asset/new',
                            focusedEdit: 'client-assets',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard paramName="matterId">
                                            <CreateRoutePage />
                                        </NumericIdGuard>
                                    }
                                    permissions={['CREATE.PARTY_ASSETS']}
                                    requiredFeature={FeatureFlag.PARTY_ASSETS}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['clientAssets'],
                            permissions: ['CREATE.PARTY_ASSETS'],
                            requiredFeature: FeatureFlag.PARTY_ASSETS,
                        },
                        {
                            path: '/contact/:contactId/client-asset/new',
                            focusedEdit: 'client-assets',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard paramName="contactId">
                                            <CreateRoutePage />
                                        </NumericIdGuard>
                                    }
                                    permissions={['CREATE.PARTY_ASSETS']}
                                    requiredFeature={FeatureFlag.PARTY_ASSETS}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['clientAssets'],
                            permissions: ['CREATE.PARTY_ASSETS'],
                            requiredFeature: FeatureFlag.PARTY_ASSETS,
                        },
                        {
                            path: '/client-asset/:id',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard>
                                            <Suspense fallback={<Loader />}>
                                                <ClientAssetPage />
                                            </Suspense>
                                        </NumericIdGuard>
                                    }
                                    permissions={['READ.PARTY_ASSETS']}
                                    requiredFeature={FeatureFlag.PARTY_ASSETS}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['clientAssets'],
                            permissions: ['READ.PARTY_ASSETS'],
                            requiredFeature: FeatureFlag.PARTY_ASSETS,
                        },
                        {
                            path: '/client-asset/:id/edit',
                            focusedEdit: 'client-assets',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard>
                                            <Suspense fallback={<Loader />}>
                                                <ClientAssetPageEdit />
                                            </Suspense>
                                        </NumericIdGuard>
                                    }
                                    permissions={['UPDATE.PARTY_ASSETS']}
                                    requiredFeature={FeatureFlag.PARTY_ASSETS}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['clientAssets'],
                            permissions: ['UPDATE.PARTY_ASSETS'],
                            requiredFeature: FeatureFlag.PARTY_ASSETS,
                        },
                        {
                            path: '/settings',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['settings'],
                            searchableName: 'menu.settings',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/settings/classifiers',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['settings'],
                            searchableName: 'menu.classifiers',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/settings/roles',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['settings'],
                            searchableName: 'menu.company-roles',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/settings/document-templates',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['settings'],
                            searchableName: 'menu.document-templates',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/settings/feature-flags',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['settings'],
                            searchableName: 'menu.feature-flags',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/settings/configuration',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['settings'],
                            searchableName: 'menu.configuration',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/settings/roles-and-permissions',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['settings'],
                            searchableName: 'menu.roles-and-permissions',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/settings/data-integrations',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['settings'],
                            searchableName: 'menu.data-integrations',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/settings/compliance',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['settings'],
                            searchableName: 'menu.compliance-blocking-rules',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/settings/processes',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['RUN.PROCESSES']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['settings'],
                            searchableName: 'menu.processes',
                            permissions: ['RUN.PROCESSES'],
                        },
                        // The Admin area: each page again under `/admin`, with the element and the gate of
                        // its old path, which stays registered. Only `/admin` itself differs by design.
                        {
                            path: '/admin',
                            element: <AdminRoute />,
                            breadcrumbs: [ADMIN_BREADCRUMB],
                        },
                        {
                            path: '/admin/classifiers',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: [ADMIN_BREADCRUMB, { path: '/admin/classifiers', title: 'menu.classifiers' }],
                            searchableName: 'menu.classifiers',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/admin/roles',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: [ADMIN_BREADCRUMB, { path: '/admin/roles', title: 'menu.company-roles' }],
                            searchableName: 'menu.company-roles',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/admin/document-templates',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: [
                                ADMIN_BREADCRUMB,
                                { path: '/admin/document-templates', title: 'menu.document-templates' },
                            ],
                            searchableName: 'menu.document-templates',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/admin/feature-flags',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: [
                                ADMIN_BREADCRUMB,
                                { path: '/admin/feature-flags', title: 'menu.feature-flags' },
                            ],
                            searchableName: 'menu.feature-flags',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/admin/configuration',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: [
                                ADMIN_BREADCRUMB,
                                { path: '/admin/configuration', title: 'menu.configuration' },
                            ],
                            searchableName: 'menu.configuration',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/admin/roles-and-permissions',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: [
                                ADMIN_BREADCRUMB,
                                { path: '/admin/roles-and-permissions', title: 'menu.roles-and-permissions' },
                            ],
                            searchableName: 'menu.roles-and-permissions',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/admin/data-integrations',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: [
                                ADMIN_BREADCRUMB,
                                { path: '/admin/data-integrations', title: 'menu.data-integrations' },
                            ],
                            searchableName: 'menu.data-integrations',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/admin/compliance',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['READ.BACK_OFFICE_SETTINGS']}
                                />
                            ),
                            breadcrumbs: [
                                ADMIN_BREADCRUMB,
                                { path: '/admin/compliance', title: 'menu.compliance-blocking-rules' },
                            ],
                            searchableName: 'menu.compliance-blocking-rules',
                            permissions: ['READ.BACK_OFFICE_SETTINGS'],
                        },
                        {
                            path: '/admin/processes',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<Settings />}
                                    permissions={['RUN.PROCESSES']}
                                />
                            ),
                            breadcrumbs: [ADMIN_BREADCRUMB, { path: '/admin/processes', title: 'menu.processes' }],
                            searchableName: 'menu.processes',
                            permissions: ['RUN.PROCESSES'],
                        },
                        {
                            path: '/admin/job-roles',
                            element: <JobRolesListPage />,
                            breadcrumbs: [ADMIN_BREADCRUMB, { path: '/admin/job-roles', title: 'job-roles.job-roles' }],
                            searchableName: 'menu.job-roles',
                            permissions: ['READ.JOB_ROLES'],
                        },
                        {
                            path: '/admin/job-roles/:id',
                            element: (
                                <NumericIdGuard>
                                    <JobRolePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: [ADMIN_BREADCRUMB, { path: '/admin/job-roles', title: 'job-roles.job-roles' }],
                            searchableName: 'command-palette.job-role-details',
                            permissions: ['READ.JOB_ROLES'],
                        },
                        {
                            path: '/admin/job-roles/:id/:tab',
                            element: (
                                <NumericIdGuard>
                                    <JobRolePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: [ADMIN_BREADCRUMB, { path: '/admin/job-roles', title: 'job-roles.job-roles' }],
                            permissions: ['READ.JOB_ROLES'],
                        },
                        {
                            path: '/admin/job-roles/:id/edit',
                            focusedEdit: 'job-roles',
                            element: (
                                <NumericIdGuard>
                                    <JobRolePageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: [ADMIN_BREADCRUMB, { path: '/admin/job-roles', title: 'job-roles.job-roles' }],
                            searchableName: 'command-palette.job-role-edit',
                            permissions: ['UPDATE.JOB_ROLES'],
                        },
                        {
                            path: '/admin/job-roles/new',
                            focusedEdit: 'job-roles',
                            element: <CreateRoutePage />,
                            breadcrumbs: [ADMIN_BREADCRUMB, { path: '/admin/job-roles', title: 'job-roles.job-roles' }],
                        },
                        {
                            path: '/admin/job-positions',
                            element: <JobPositionsListPage />,
                            breadcrumbs: [
                                ADMIN_BREADCRUMB,
                                { path: '/admin/job-positions', title: 'job-positions.job-positions' },
                            ],
                            searchableName: 'menu.job-positions',
                            permissions: ['READ.JOB_POSITION'],
                        },
                        {
                            path: '/admin/job-positions/:id',
                            element: (
                                <NumericIdGuard>
                                    <JobPositionPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: [
                                ADMIN_BREADCRUMB,
                                { path: '/admin/job-positions', title: 'job-positions.job-positions' },
                            ],
                            searchableName: 'command-palette.job-position-details',
                            permissions: ['READ.JOB_POSITION'],
                        },
                        {
                            path: '/admin/job-positions/:id/:tab',
                            element: (
                                <NumericIdGuard>
                                    <JobPositionPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: [
                                ADMIN_BREADCRUMB,
                                { path: '/admin/job-positions', title: 'job-positions.job-positions' },
                            ],
                            permissions: ['READ.JOB_POSITION'],
                        },
                        {
                            path: '/admin/job-positions/:id/edit',
                            focusedEdit: 'job-positions',
                            element: (
                                <NumericIdGuard>
                                    <JobPositionPageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: [
                                ADMIN_BREADCRUMB,
                                { path: '/admin/job-positions', title: 'job-positions.job-positions' },
                            ],
                            searchableName: 'command-palette.job-position-edit',
                            permissions: ['UPDATE.JOB_POSITION'],
                        },
                        {
                            path: '/admin/job-positions/new',
                            focusedEdit: 'job-positions',
                            element: <CreateRoutePage />,
                            breadcrumbs: [
                                ADMIN_BREADCRUMB,
                                { path: '/admin/job-positions', title: 'job-positions.job-positions' },
                            ],
                        },
                        {
                            path: '/admin/financials',
                            element: <FinancialConditionsPage />,
                            breadcrumbs: [
                                ADMIN_BREADCRUMB,
                                { path: '/admin/financials', title: 'financial-conditions.financial-conditions' },
                            ],
                            searchableName: 'menu.financial-conditions',
                            permissions: ['READ.FINANCIAL_CONDITIONS'],
                        },
                        {
                            path: '/admin/country-management',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<CountryManagementPage />}
                                    permissions={['READ.BACK_OFFICE_COUNTRY_MANAGEMENT']}
                                />
                            ),
                            breadcrumbs: [
                                ADMIN_BREADCRUMB,
                                { path: '/admin/country-management', title: 'menu.country-management' },
                            ],
                            searchableName: 'menu.country-management',
                            permissions: ['READ.BACK_OFFICE_COUNTRY_MANAGEMENT'],
                        },
                        {
                            path: '/admin/audit-trail',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<AuditTrailListPage />}
                                    permissions={['READ.AUDIT_LOGS']}
                                />
                            ),
                            breadcrumbs: [ADMIN_BREADCRUMB, { path: '/admin/audit-trail', title: 'audit-trail.title' }],
                            searchableName: 'audit-trail.title',
                            permissions: ['READ.AUDIT_LOGS'],
                        },
                        // Of the Admin area alone: no old path opens this page.
                        {
                            path: '/admin/firm-widgets',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<FirmWidgetsPage />}
                                    permissions={SHARE_WITH_FIRM_PERMISSIONS}
                                />
                            ),
                            breadcrumbs: [
                                ADMIN_BREADCRUMB,
                                { path: '/admin/firm-widgets', title: 'home.widgets.firm.title' },
                            ],
                            searchableName: 'home.widgets.firm.title',
                            permissions: SHARE_WITH_FIRM_PERMISSIONS,
                        },
                        {
                            path: 'matters',
                            element: <MattersListPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['matters'],
                            searchableName: 'matters.matters',
                            permissions: [
                                'READ.MATTERS.MY_CASES',
                                'READ.MATTERS.MY_TEAM',
                                'READ.MATTERS.MY_TEAM.NON_CONFIDENTIAL',
                                'READ.MATTERS.CONFIDENTIAL',
                                'READ.MATTERS.NON_CONFIDENTIAL',
                                'READ.MATTERS.NON_CONFIDENTIAL_MY_CONTACTS',
                            ],
                        },
                        {
                            path: 'matters/new-cases',
                            element: <MattersListPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['matters'],
                            permissions: [
                                'READ.MATTERS.MY_CASES',
                                'READ.MATTERS.MY_TEAM',
                                'READ.MATTERS.MY_TEAM.NON_CONFIDENTIAL',
                                'READ.MATTERS.CONFIDENTIAL',
                                'READ.MATTERS.NON_CONFIDENTIAL',
                                'READ.MATTERS.NON_CONFIDENTIAL_MY_CONTACTS',
                            ],
                        },
                        {
                            path: '/matter/:id',
                            element: (
                                <NumericIdGuard>
                                    <MatterPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['matters'],
                            searchableName: 'command-palette.matter-details',
                            permissions: [
                                'READ.MATTERS.MY_CASES',
                                'READ.MATTERS.MY_TEAM',
                                'READ.MATTERS.MY_TEAM.NON_CONFIDENTIAL',
                                'READ.MATTERS.CONFIDENTIAL',
                                'READ.MATTERS.NON_CONFIDENTIAL',
                                'READ.MATTERS.NON_CONFIDENTIAL_MY_CONTACTS',
                            ],
                        },
                        {
                            path: '/matter/:id/:tab',
                            element: (
                                <NumericIdGuard>
                                    <MatterPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['matters'],
                        },
                        {
                            path: '/matter/:id/invoices/:invoiceTab',
                            element: (
                                <NumericIdGuard>
                                    <MatterPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['matters'],
                        },
                        {
                            path: '/matter/new',
                            focusedEdit: 'matters',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['matters'],
                        },
                        {
                            path: MATTER_EDIT_ROUTE_PATHS.EDIT,
                            focusedEdit: 'matters',
                            element: (
                                <NumericIdGuard>
                                    <MatterPageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['matters'],
                        },
                        {
                            path: '/matter/copy/:id',
                            focusedEdit: 'matters',
                            element: (
                                <NumericIdGuard>
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['matters'],
                        },
                        {
                            path: '/matter/:matterId/timecards/new',
                            focusedEdit: 'timecards',
                            element: (
                                <NumericIdGuard paramName="matterId">
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['matters'],
                        },
                        {
                            // Add Timecard from a My Portfolio row 3-dots menu — the
                            // Case/Client picker is pre-filtered to the contact's cases.
                            path: '/contact/:contactId/timecards/new',
                            focusedEdit: 'timecards',
                            element: (
                                <NumericIdGuard paramName="contactId">
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['matters'],
                        },
                        {
                            path: '/projects',
                            element: <ProjectsListPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['projects'],
                            searchableName: 'projects.projects',
                            permissions: ['READ.PROJECTS'],
                        },
                        {
                            path: '/projects/:id',
                            element: (
                                <NumericIdGuard>
                                    <ProjectPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['projects'],
                            searchableName: 'command-palette.project-details',
                            permissions: ['READ.PROJECTS'],
                        },
                        {
                            path: '/projects/:id/:tab',
                            element: (
                                <NumericIdGuard>
                                    <ProjectPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['projects'],
                        },
                        {
                            path: '/projects/new',
                            focusedEdit: 'projects',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['projects'],
                        },
                        {
                            path: '/projects/:id/edit',
                            focusedEdit: 'projects',
                            element: (
                                <NumericIdGuard>
                                    <ProjectPageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['projects'],
                        },
                        {
                            path: '/delegations',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <Suspense fallback={<Loader />}>
                                            <DelegationsPage />
                                        </Suspense>
                                    }
                                    permissions={[
                                        'VIEW.DELEGATIONS.MY_OWN',
                                        'VIEW.DELEGATIONS.MY_TEAM',
                                        'VIEW.DELEGATIONS.MY_CORE_TEAM',
                                        'VIEW.DELEGATIONS.ALL',
                                    ]}
                                    requiredFeature={FeatureFlag.DELEGATION}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['delegations'],
                            searchableName: 'menu.delegations',
                            permissions: [
                                'VIEW.DELEGATIONS.MY_OWN',
                                'VIEW.DELEGATIONS.MY_TEAM',
                                'VIEW.DELEGATIONS.MY_CORE_TEAM',
                                'VIEW.DELEGATIONS.ALL',
                            ],
                            requiredFeature: FeatureFlag.DELEGATION,
                        },
                        {
                            path: '/delegations/new',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <Suspense fallback={<Loader />}>
                                            <DelegationWizardPage />
                                        </Suspense>
                                    }
                                    permissions={[
                                        'MANAGE.DELEGATIONS.MY_OWN',
                                        'MANAGE.DELEGATIONS.MY_TEAM',
                                        'MANAGE.DELEGATIONS.ALL',
                                    ]}
                                    requiredFeature={FeatureFlag.DELEGATION}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['delegations'],
                            requiredFeature: FeatureFlag.DELEGATION,
                        },
                        {
                            path: CONTACT_MERGE_PATH,
                            element: contactMergeRouteElement,
                            breadcrumbs: BREADCRUMB_COLLECTION['contacts'],
                            requiredFeature: FeatureFlag.CONTACT_MERGING,
                        },
                        {
                            path: '/delegations/:id',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard>
                                            <Suspense fallback={<Loader />}>
                                                <DelegationDetailPage />
                                            </Suspense>
                                        </NumericIdGuard>
                                    }
                                    permissions={[
                                        'VIEW.DELEGATIONS.MY_OWN',
                                        'VIEW.DELEGATIONS.MY_TEAM',
                                        'VIEW.DELEGATIONS.MY_CORE_TEAM',
                                        'VIEW.DELEGATIONS.ALL',
                                    ]}
                                    requiredFeature={FeatureFlag.DELEGATION}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['delegations'],
                            searchableName: 'command-palette.delegation-details',
                            permissions: [
                                'VIEW.DELEGATIONS.MY_OWN',
                                'VIEW.DELEGATIONS.MY_TEAM',
                                'VIEW.DELEGATIONS.MY_CORE_TEAM',
                                'VIEW.DELEGATIONS.ALL',
                            ],
                            requiredFeature: FeatureFlag.DELEGATION,
                        },
                        {
                            path: '/delegations/:id/cases',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard>
                                            <Suspense fallback={<Loader />}>
                                                <DelegationEditCasesPage />
                                            </Suspense>
                                        </NumericIdGuard>
                                    }
                                    permissions={[
                                        'MANAGE.DELEGATIONS.MY_OWN',
                                        'MANAGE.DELEGATIONS.MY_TEAM',
                                        'MANAGE.DELEGATIONS.ALL',
                                    ]}
                                    requiredFeature={FeatureFlag.DELEGATION}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['delegations'],
                            requiredFeature: FeatureFlag.DELEGATION,
                        },
                        {
                            path: '/experience-records',
                            element: <ExperienceRecordListPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['experienceRecords'],
                            searchableName: 'experience-records.experience-records',
                            permissions: ['READ.EXPERIENCE_RECORDS.ALL'],
                        },
                        {
                            path: '/experience-records/:id',
                            element: (
                                <NumericIdGuard>
                                    <ExperienceRecordPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['experienceRecords'],
                            searchableName: 'command-palette.experience-record-details',
                            permissions: ['READ.EXPERIENCE_RECORDS.ALL'],
                        },
                        {
                            path: '/experience-records/new',
                            focusedEdit: 'experience',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['experienceRecords'],
                        },
                        {
                            path: '/matter/:matterId/experience-records/new',
                            focusedEdit: 'experience',
                            element: (
                                <NumericIdGuard paramName="matterId">
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['experienceRecords'],
                        },
                        {
                            path: '/contact/:contactId/experience-records/new',
                            focusedEdit: 'experience',
                            element: (
                                <NumericIdGuard paramName="contactId">
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['experienceRecords'],
                        },
                        {
                            path: '/experience-records/:id/edit',
                            focusedEdit: 'experience',
                            element: (
                                <NumericIdGuard>
                                    <ExperienceRecordPageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['experienceRecords'],
                        },
                        {
                            path: '/employees',
                            element: <EmployeesPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['employees'],
                            searchableName: 'menu.colleagues',
                            permissions: ['READ.EMPLOYEES'],
                        },
                        {
                            path: '/employee/:id',
                            element: (
                                <NumericIdGuard>
                                    <EmployeePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['employees'],
                            searchableName: 'command-palette.employee-details',
                            permissions: ['READ.EMPLOYEES'],
                        },
                        {
                            path: '/employee/:id/:tab',
                            element: (
                                <NumericIdGuard>
                                    <EmployeePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['employees'],
                            permissions: ['READ.EMPLOYEES'],
                        },
                        {
                            path: '/employee/:id/edit',
                            focusedEdit: 'employees',
                            element: (
                                <NumericIdGuard>
                                    <EmployeePageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['employees'],
                            searchableName: 'command-palette.employee-edit',
                            permissions: ['UPDATE.EMPLOYEES', 'UPDATE.EMPLOYEE_PERSONAL.ALL'],
                        },
                        {
                            path: '/employee/new',
                            focusedEdit: 'employees',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['employees'],
                        },
                        {
                            path: '/debriefings',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <Suspense fallback={<Loader />}>
                                            <DebriefingsListPage />
                                        </Suspense>
                                    }
                                    permissions={['READ.DEBRIEFINGS.ALL']}
                                    requiredFeature={FeatureFlag.DEBRIEFING_MODULE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['debriefings'],
                            searchableName: 'debriefing.debriefings',
                            permissions: ['READ.DEBRIEFINGS.ALL'],
                            requiredFeature: FeatureFlag.DEBRIEFING_MODULE,
                        },
                        {
                            path: '/debriefing/new',
                            focusedEdit: 'debriefings',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<CreateRoutePage />}
                                    permissions={['CREATE.DEBRIEFINGS']}
                                    requiredFeature={FeatureFlag.DEBRIEFING_MODULE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['debriefings'],
                            permissions: ['CREATE.DEBRIEFINGS'],
                            requiredFeature: FeatureFlag.DEBRIEFING_MODULE,
                        },
                        {
                            path: '/matter/:matterId/debriefing/new',
                            focusedEdit: 'debriefings',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard paramName="matterId">
                                            <CreateRoutePage />
                                        </NumericIdGuard>
                                    }
                                    permissions={['CREATE.DEBRIEFINGS']}
                                    requiredFeature={FeatureFlag.DEBRIEFING_MODULE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['debriefings'],
                            permissions: ['CREATE.DEBRIEFINGS'],
                            requiredFeature: FeatureFlag.DEBRIEFING_MODULE,
                        },
                        {
                            path: '/contact/:contactId/debriefing/new',
                            focusedEdit: 'debriefings',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard paramName="contactId">
                                            <CreateRoutePage />
                                        </NumericIdGuard>
                                    }
                                    permissions={['CREATE.DEBRIEFINGS']}
                                    requiredFeature={FeatureFlag.DEBRIEFING_MODULE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['debriefings'],
                            permissions: ['CREATE.DEBRIEFINGS'],
                            requiredFeature: FeatureFlag.DEBRIEFING_MODULE,
                        },
                        {
                            path: '/debriefing/:id',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard>
                                            <Suspense fallback={<Loader />}>
                                                <DebriefingPage />
                                            </Suspense>
                                        </NumericIdGuard>
                                    }
                                    permissions={['READ.DEBRIEFINGS.ALL']}
                                    requiredFeature={FeatureFlag.DEBRIEFING_MODULE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['debriefings'],
                            permissions: ['READ.DEBRIEFINGS.ALL'],
                            requiredFeature: FeatureFlag.DEBRIEFING_MODULE,
                        },
                        {
                            path: '/debriefing/:id/:tab',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard>
                                            <Suspense fallback={<Loader />}>
                                                <DebriefingPage />
                                            </Suspense>
                                        </NumericIdGuard>
                                    }
                                    permissions={['READ.DEBRIEFINGS.ALL']}
                                    requiredFeature={FeatureFlag.DEBRIEFING_MODULE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['debriefings'],
                        },
                        {
                            path: '/debriefing/:debriefingId/task/new',
                            focusedEdit: 'tasks',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard paramName="debriefingId">
                                            <CreateRoutePage />
                                        </NumericIdGuard>
                                    }
                                    permissions={['CREATE.TASKS']}
                                    requiredFeature={FeatureFlag.DEBRIEFING_MODULE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['debriefings'],
                        },
                        {
                            path: '/debriefing/:id/edit',
                            focusedEdit: 'debriefings',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard>
                                            <Suspense fallback={<Loader />}>
                                                <DebriefingPageEdit />
                                            </Suspense>
                                        </NumericIdGuard>
                                    }
                                    permissions={['UPDATE.DEBRIEFINGS']}
                                    requiredFeature={FeatureFlag.DEBRIEFING_MODULE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['debriefings'],
                            permissions: ['UPDATE.DEBRIEFINGS'],
                            requiredFeature: FeatureFlag.DEBRIEFING_MODULE,
                        },
                        {
                            path: '/trainings',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <Suspense fallback={<Loader />}>
                                            <TrainingsListPage />
                                        </Suspense>
                                    }
                                    permissions={['READ.TRAININGS.ALL']}
                                    requiredFeature={FeatureFlag.TRAININGS_MODULE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['trainings'],
                            searchableName: 'trainings.title',
                            permissions: ['READ.TRAININGS.ALL'],
                            requiredFeature: FeatureFlag.TRAININGS_MODULE,
                        },
                        {
                            path: '/trainings/new',
                            focusedEdit: 'trainings',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<CreateRoutePage />}
                                    permissions={['CREATE.TRAININGS.ALL']}
                                    requiredFeature={FeatureFlag.TRAININGS_MODULE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['trainings'],
                            permissions: ['CREATE.TRAININGS.ALL'],
                            requiredFeature: FeatureFlag.TRAININGS_MODULE,
                        },
                        {
                            path: '/reports',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<ReportsHubPage />}
                                    permissions={['READ.REPORTS.ALL']}
                                    requiredFeature={FeatureFlag.PORTAL_REPORTS}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['reports'],
                            searchableName: 'reporting.title',
                            permissions: ['READ.REPORTS.ALL'],
                            requiredFeature: FeatureFlag.PORTAL_REPORTS,
                        },
                        {
                            path: '/reports/:category',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<ReportsHubPage />}
                                    permissions={['READ.REPORTS.ALL']}
                                    requiredFeature={FeatureFlag.PORTAL_REPORTS}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['reports'],
                            permissions: ['READ.REPORTS.ALL'],
                            requiredFeature: FeatureFlag.PORTAL_REPORTS,
                        },
                        {
                            path: '/reports/:category/:reportKey',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<ReportRoute />}
                                    permissions={['READ.REPORTS.ALL']}
                                    requiredFeature={FeatureFlag.PORTAL_REPORTS}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['reports'],
                            permissions: ['READ.REPORTS.ALL'],
                            requiredFeature: FeatureFlag.PORTAL_REPORTS,
                        },
                        {
                            path: '/trainings/reports',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <Suspense fallback={<Loader />}>
                                            <TrainingsReportsPage />
                                        </Suspense>
                                    }
                                    permissions={['READ.TRAININGS.ALL']}
                                    requiredFeature={FeatureFlag.TRAININGS_MODULE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['trainingsReports'],
                            searchableName: 'trainings.reports.title',
                            permissions: ['READ.TRAININGS.ALL'],
                            requiredFeature: FeatureFlag.TRAININGS_MODULE,
                        },
                        {
                            path: '/trainings/:id',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard>
                                            <Suspense fallback={<Loader />}>
                                                <TrainingReadPage />
                                            </Suspense>
                                        </NumericIdGuard>
                                    }
                                    permissions={['READ.TRAININGS.ALL']}
                                    requiredFeature={FeatureFlag.TRAININGS_MODULE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['trainings'],
                            searchableName: 'trainings.read.title',
                            permissions: ['READ.TRAININGS.ALL'],
                            requiredFeature: FeatureFlag.TRAININGS_MODULE,
                        },
                        {
                            path: '/trainings/:id/edit',
                            focusedEdit: 'trainings',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <NumericIdGuard>
                                            <Suspense fallback={<Loader />}>
                                                <TrainingPageEdit />
                                            </Suspense>
                                        </NumericIdGuard>
                                    }
                                    permissions={['UPDATE.TRAININGS.ALL']}
                                    requiredFeature={FeatureFlag.TRAININGS_MODULE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['trainings'],
                            permissions: ['UPDATE.TRAININGS.ALL'],
                            requiredFeature: FeatureFlag.TRAININGS_MODULE,
                        },
                        {
                            path: 'efforts',
                            element: <EffortsPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['efforts'],
                            searchableName: 'menu.efforts',
                            permissions: ['READ.EFFORTS.ALL', 'READ.EFFORTS.MY_OWN'],
                        },
                        {
                            path: 'rankings-referees',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={
                                        <Suspense fallback={<Loader />}>
                                            <RankingsRefereesPage />
                                        </Suspense>
                                    }
                                    permissions={['READ.EFFORTS.ALL', 'READ.EFFORTS.MY_OWN']}
                                    requiredFeature={FeatureFlag.RANKINGS_REFEREE}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['rankingsReferees'],
                            searchableName: 'menu.rankings-referees',
                            permissions: ['READ.EFFORTS.ALL', 'READ.EFFORTS.MY_OWN'],
                            requiredFeature: FeatureFlag.RANKINGS_REFEREE,
                        },
                        {
                            path: 'effort/:id',
                            element: (
                                <NumericIdGuard>
                                    <EffortPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['efforts'],
                            searchableName: 'command-palette.effort-details',
                            permissions: ['READ.EFFORTS.ALL', 'READ.EFFORTS.MY_OWN'],
                        },
                        {
                            path: 'effort/:id/edit',
                            focusedEdit: 'efforts',
                            element: (
                                <NumericIdGuard>
                                    <EffortPageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['efforts'],
                            searchableName: 'command-palette.effort-edit',
                            permissions: ['UPDATE.EFFORTS.ALL', 'UPDATE.EFFORTS.MY_OWN'],
                        },
                        {
                            path: 'effort/:id/:tab',
                            element: (
                                <NumericIdGuard>
                                    <EffortPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['efforts'],
                            permissions: ['READ.EFFORTS.ALL', 'READ.EFFORTS.MY_OWN'],
                        },
                        {
                            path: '/effort/new',
                            focusedEdit: 'efforts',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['efforts'],
                        },
                        {
                            path: 'my-portfolio',
                            element: <MyPortfolioPage />,
                            searchableName: 'portfolio.my-portfolio',
                            breadcrumbs: BREADCRUMB_COLLECTION['myPortfolio'],
                            permissions: [
                                'READ.PARTIES.ALL',
                                'READ.PARTIES.MY_CONTACTS',
                                'READ.PARTIES.RELATED_TO_MY_CASES',
                            ],
                        },
                        {
                            path: 'law-firms',
                            element: <LawFirmsPage />,
                            searchableName: 'menu.law-firms-hub',
                            breadcrumbs: BREADCRUMB_COLLECTION['lawFirms'],
                            permissions: ['READ.PARTIES.ALL'],
                            requiredFeature: FeatureFlag.GLOBAL_LAW_FIRMS_HUB,
                        },
                        {
                            path: 'expenses',
                            element: <ExpensesPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['expenses'],
                            searchableName: 'menu.expenses',
                            permissions: ['READ.EXPENSES.ALL', 'READ.EXPENSES.MY_OWN', 'READ.EXPENSES.MATTER_ACCESS'],
                        },
                        {
                            path: 'expense/:id',
                            element: (
                                <NumericIdGuard>
                                    <ExpensePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['expenses'],
                            permissions: ['READ.EXPENSES.ALL', 'READ.EXPENSES.MY_OWN', 'READ.EXPENSES.MATTER_ACCESS'],
                        },
                        {
                            path: 'expense/:id/edit',
                            focusedEdit: 'expenses',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/404"
                                    element={
                                        <NumericIdGuard>
                                            <ExpensePageEdit />
                                        </NumericIdGuard>
                                    }
                                    permissions={[
                                        'UPDATE.EXPENSES.ALL',
                                        'UPDATE.EXPENSES.MATTER_ACCESS',
                                        'UPDATE.EXPENSES.MY_OWN',
                                    ]}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['expenses'],
                            permissions: [
                                'UPDATE.EXPENSES.ALL',
                                'UPDATE.EXPENSES.MATTER_ACCESS',
                                'UPDATE.EXPENSES.MY_OWN',
                            ],
                        },
                        {
                            path: 'expense/:id/:tab',
                            element: (
                                <NumericIdGuard>
                                    <ExpensePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['expenses'],
                            permissions: ['READ.EXPENSES.ALL', 'READ.EXPENSES.MY_OWN', 'READ.EXPENSES.MATTER_ACCESS'],
                        },
                        {
                            path: '/expense/new',
                            focusedEdit: 'expenses',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/404"
                                    element={<CreateRoutePage />}
                                    permissions={['CREATE.EXPENSES']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['expenses'],
                            permissions: ['CREATE.EXPENSES'],
                        },
                        {
                            path: 'groups',
                            element: <GroupsPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['groups'],
                            searchableName: 'menu.groups',
                            permissions: ['READ.PARTY_GROUPS.ALL', 'READ.PARTY_GROUPS.MY_OWN'],
                        },
                        {
                            path: '/group/:id',
                            element: (
                                <NumericIdGuard>
                                    <GroupPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['groups'],
                            searchableName: 'command-palette.group-details',
                            permissions: ['READ.PARTY_GROUPS.ALL', 'READ.PARTY_GROUPS.MY_OWN'],
                        },
                        {
                            path: '/group/:id/:tab',
                            element: (
                                <NumericIdGuard>
                                    <GroupPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['groups'],
                        },
                        {
                            path: '/group/new',
                            focusedEdit: 'groups',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['groups'],
                        },
                        {
                            path: '/group/:id/edit',
                            focusedEdit: 'groups',
                            element: (
                                <NumericIdGuard>
                                    <GroupPageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['groups'],
                        },
                        {
                            path: '/contact/:contactId/effort/new',
                            focusedEdit: 'efforts',
                            element: (
                                <NumericIdGuard paramName="contactId">
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['contacts'],
                        },
                        {
                            path: 'offices',
                            element: <OfficestPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['offices'],
                            searchableName: 'offices.offices',
                            permissions: ['READ.OFFICES'],
                        },
                        {
                            path: '/office/:id',
                            element: (
                                <NumericIdGuard>
                                    <OfficePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['offices'],
                            searchableName: 'command-palette.office-details',
                            permissions: ['READ.OFFICES'],
                        },
                        {
                            path: '/office/:id/:tab',
                            element: (
                                <NumericIdGuard>
                                    <OfficePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['offices'],
                            permissions: ['READ.OFFICES'],
                        },
                        {
                            path: '/office/:id/edit',
                            focusedEdit: 'offices',
                            element: (
                                <NumericIdGuard>
                                    <OfficePageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['offices'],
                            searchableName: 'command-palette.office-edit',
                            permissions: ['UPDATE.OFFICES'],
                        },
                        {
                            path: '/office/new',
                            focusedEdit: 'offices',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['offices'],
                        },
                        {
                            path: 'practice-areas',
                            element: <PracticeAreasListPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['practiceAreas'],
                            searchableName: 'menu.practice-areas',
                            permissions: ['READ.PRACTICE_AREAS'],
                        },
                        {
                            path: '/practice-area/:id',
                            element: (
                                <NumericIdGuard>
                                    <PracticeAreaPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['practiceAreas'],
                            searchableName: 'command-palette.practice-area-details',
                            permissions: ['READ.PRACTICE_AREAS'],
                        },
                        {
                            path: '/practice-area/:id/:tab',
                            element: (
                                <NumericIdGuard>
                                    <PracticeAreaPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['practiceAreas'],
                            permissions: ['READ.PRACTICE_AREAS'],
                        },
                        {
                            path: '/practice-area/:id/edit',
                            focusedEdit: 'practice-areas',
                            element: (
                                <NumericIdGuard>
                                    <PracticeAreaPageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['practiceAreas'],
                            searchableName: 'command-palette.practice-area-edit',
                            permissions: ['UPDATE.PRACTICE_AREAS'],
                        },
                        {
                            path: '/practice-area/new',
                            focusedEdit: 'practice-areas',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['practiceAreas'],
                        },
                        {
                            path: 'teams',
                            element: <TeamsListPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['teams'],
                            searchableName: 'teams.teams',
                            permissions: ['READ.TEAMS'],
                        },
                        {
                            path: '/team/:id',
                            element: (
                                <NumericIdGuard>
                                    <TeamPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['teams'],
                            searchableName: 'command-palette.team-details',
                            permissions: ['READ.TEAMS'],
                        },
                        {
                            path: '/team/:id/:tab',
                            element: (
                                <NumericIdGuard>
                                    <TeamPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['teams'],
                            permissions: ['READ.TEAMS'],
                        },
                        {
                            path: '/team/:id/edit',
                            focusedEdit: 'teams',
                            element: (
                                <NumericIdGuard>
                                    <TeamPageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['teams'],
                            searchableName: 'command-palette.team-edit',
                            permissions: ['UPDATE.TEAMS'],
                        },
                        {
                            path: '/team/new',
                            focusedEdit: 'teams',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['teams'],
                        },
                        {
                            path: 'invoices',
                            element: <InvoicesPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['invoices'],
                            searchableName: 'menu.invoices',
                            permissions: ['READ.INVOICES.MATTER_ACCESS'],
                        },
                        {
                            path: 'invoices/:invoiceTab',
                            element: <InvoicesPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['invoices'],
                            permissions: ['READ.INVOICES.MATTER_ACCESS'],
                        },
                        {
                            path: INVOICE_DETAIL_ROUTE_PATHS.STANDALONE,
                            element: (
                                <NumericIdGuard>
                                    <InvoicePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['invoices'],
                            searchableName: 'command-palette.invoice-details',
                            permissions: ['READ.INVOICES.MATTER_ACCESS'],
                        },
                        {
                            path: INVOICE_DETAIL_ROUTE_PATHS.STANDALONE_TAB,
                            element: (
                                <NumericIdGuard>
                                    <InvoicePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['invoices'],
                            permissions: ['READ.INVOICES.MATTER_ACCESS'],
                        },
                        {
                            path: '/invoice/:invoiceId/matter/:matterId/assignment/:assignmentId/timecards/new',
                            focusedEdit: 'timecards',
                            element: (
                                <NumericIdGuard paramName={['invoiceId', 'matterId', 'assignmentId']}>
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['invoices'],
                        },
                        {
                            path: '/invoice/:invoiceId/matter/:matterId/timecards/new',
                            focusedEdit: 'timecards',
                            element: (
                                <NumericIdGuard paramName={['invoiceId', 'matterId']}>
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['invoices'],
                        },
                        {
                            path: '/invoice/:invoiceId/matter/:matterId/expenses/new',
                            focusedEdit: 'expenses',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/404"
                                    element={
                                        <NumericIdGuard paramName={['invoiceId', 'matterId']}>
                                            <CreateRoutePage />
                                        </NumericIdGuard>
                                    }
                                    permissions={['CREATE.EXPENSES']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['invoices'],
                            permissions: ['CREATE.EXPENSES'],
                        },
                        {
                            path: 'prepayments',
                            element: <PrepaymentsPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['prepayments'],
                            searchableName: 'menu.prepayments',
                            permissions: ['READ.INVOICES.MATTER_ACCESS'],
                        },
                        {
                            path: 'job-roles',
                            element: <JobRolesListPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['jobRoles'],
                            searchableName: 'menu.job-roles',
                            permissions: ['READ.JOB_ROLES'],
                        },
                        {
                            path: '/job-role/:id',
                            element: (
                                <NumericIdGuard>
                                    <JobRolePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['jobRoles'],
                            searchableName: 'command-palette.job-role-details',
                            permissions: ['READ.JOB_ROLES'],
                        },
                        {
                            path: '/job-role/:id/:tab',
                            element: (
                                <NumericIdGuard>
                                    <JobRolePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['jobRoles'],
                            permissions: ['READ.JOB_ROLES'],
                        },
                        {
                            path: '/job-role/:id/edit',
                            focusedEdit: 'job-roles',
                            element: (
                                <NumericIdGuard>
                                    <JobRolePageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['jobRoles'],
                            searchableName: 'command-palette.job-role-edit',
                            permissions: ['UPDATE.JOB_ROLES'],
                        },
                        {
                            path: '/job-role/new',
                            focusedEdit: 'job-roles',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['jobRoles'],
                        },
                        {
                            path: 'job-positions',
                            element: <JobPositionsListPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['jobPositions'],
                            searchableName: 'menu.job-positions',
                            permissions: ['READ.JOB_POSITION'],
                        },
                        {
                            path: '/job-position/:id',
                            element: (
                                <NumericIdGuard>
                                    <JobPositionPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['jobPositions'],
                            searchableName: 'command-palette.job-position-details',
                            permissions: ['READ.JOB_POSITION'],
                        },
                        {
                            path: '/job-position/:id/:tab',
                            element: (
                                <NumericIdGuard>
                                    <JobPositionPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['jobPositions'],
                            permissions: ['READ.JOB_POSITION'],
                        },
                        {
                            path: '/job-position/:id/edit',
                            focusedEdit: 'job-positions',
                            element: (
                                <NumericIdGuard>
                                    <JobPositionPageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['jobPositions'],
                            searchableName: 'command-palette.job-position-edit',
                            permissions: ['UPDATE.JOB_POSITION'],
                        },
                        {
                            path: '/job-position/new',
                            focusedEdit: 'job-positions',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['jobPositions'],
                        },
                        {
                            path: 'financial-conditions',
                            element: <FinancialConditionsPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['financialConditions'],
                            searchableName: 'menu.financial-conditions',
                            permissions: ['READ.FINANCIAL_CONDITIONS'],
                        },
                        {
                            path: 'country-management',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<CountryManagementPage />}
                                    permissions={['READ.BACK_OFFICE_COUNTRY_MANAGEMENT']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['countryManagement'],
                            searchableName: 'menu.country-management',
                            permissions: ['READ.BACK_OFFICE_COUNTRY_MANAGEMENT'],
                        },
                        {
                            path: 'coi-checks',
                            element: <ConflictCheckReports />,
                            breadcrumbs: BREADCRUMB_COLLECTION['coiChecks'],
                            searchableName: 'menu.coi-check',
                            permissions: ['READ.COI_CHECKS.ALL', 'READ.COI_CHECKS.MY_OWN'],
                        },
                        {
                            path: 'coi-check/:id',
                            element: (
                                <NumericIdGuard>
                                    <CoiCheckPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['coiChecks'],
                            searchableName: 'command-palette.coi-check-details',
                            permissions: ['READ.COI_CHECKS.ALL', 'READ.COI_CHECKS.MY_OWN'],
                        },
                        {
                            path: 'pricelists',
                            element: <PricelistsPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['pricelists'],
                            searchableName: 'menu.pricelists',
                            permissions: [
                                'READ.PRICELISTS.ALL',
                                'READ.PRICELISTS.MODEL',
                                'READ.PRICELISTS.NON_MODEL_WITH_ACCESS',
                            ],
                        },
                        {
                            path: '/tasks',
                            element: <TasksPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['tasks'],
                            searchableName: 'menu.tasks',
                            permissions: ['READ.TASKS.ALL', 'READ.TASKS.MY_OWN', 'READ.TASKS.CREATED_BY_ME'],
                        },
                        {
                            path: '/task/new',
                            focusedEdit: 'tasks',
                            element: <CreateRoutePage />,
                        },
                        {
                            path: '/task/new/:id',
                            focusedEdit: 'tasks',
                            element: (
                                <NumericIdGuard>
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                        },
                        {
                            path: '/task/:id',
                            element: (
                                <NumericIdGuard>
                                    <TaskPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['tasks'],
                            searchableName: 'command-palette.task-details',
                            permissions: ['READ.TASKS.ALL', 'READ.TASKS.MY_OWN', 'READ.TASKS.CREATED_BY_ME'],
                        },
                        {
                            path: '/task/:id/edit',
                            focusedEdit: 'tasks',
                            element: (
                                <NumericIdGuard>
                                    <TaskPageEdit />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['tasks'],
                            permissions: ['READ.TASKS.ALL', 'READ.TASKS.MY_OWN', 'READ.TASKS.CREATED_BY_ME'],
                        },
                        {
                            path: 'timecards',
                            element: <TimecardListPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['timecards'],
                            searchableName: 'timecards.timecards',
                            permissions: ['READ.TIMECARDS.MY_OWN', 'READ.TIMECARDS.MATTER_ACCESS'],
                        },
                        {
                            path: 'timecards/main',
                            element: <TimecardListPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['timecards'],
                        },
                        {
                            path: 'timecards/draft',
                            element: <TimecardListPage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['timecards'],
                        },
                        {
                            path: 'timecards/new',
                            focusedEdit: 'timecards',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['timecards'],
                        },
                        {
                            path: 'timecards/finish/:id',
                            focusedEdit: 'timecards',
                            element: (
                                <NumericIdGuard>
                                    <FinishTimecardPageForm />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['timecards'],
                        },
                        {
                            path: 'timecards/finish',
                            focusedEdit: 'timecards',
                            element: <CreateRoutePage />,
                            breadcrumbs: BREADCRUMB_COLLECTION['timecards'],
                        },
                        {
                            path: 'timecards/duplicate/:id',
                            focusedEdit: 'timecards',
                            element: (
                                <NumericIdGuard>
                                    <CreateRoutePage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['timecards'],
                        },
                        {
                            path: 'timecards/:id',
                            element: (
                                <NumericIdGuard>
                                    <TimecardPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['timecards'],
                            searchableName: 'command-palette.timecard-details',
                            permissions: ['READ.TIMECARDS.MY_OWN', 'READ.TIMECARDS.MATTER_ACCESS'],
                        },
                        {
                            path: 'timecards/:id/edit',
                            focusedEdit: 'timecards',
                            element: (
                                <NumericIdGuard>
                                    <TimecardEditPage />
                                </NumericIdGuard>
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['timecards'],
                        },
                        {
                            path: 'audit-trail',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<AuditTrailListPage />}
                                    permissions={['READ.AUDIT_LOGS']}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['auditTrail'],
                            searchableName: 'audit-trail.title',
                            permissions: ['READ.AUDIT_LOGS'],
                        },
                        {
                            path: '/ndf-export-hbr',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<NDFExportPage type="HBR" />}
                                    permissions={['READ.NDF']}
                                    requiredFeature={FeatureFlag.NDF_EXPORT}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['ndfHbr'],
                            searchableName: 'menu.payroll-hbr',
                            permissions: ['READ.NDF'],
                            requiredFeature: FeatureFlag.NDF_EXPORT,
                        },
                        {
                            path: '/ndf-export-erp',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<NDFExportPage type="ERP" />}
                                    permissions={['READ.NDF']}
                                    requiredFeature={FeatureFlag.NDF_EXPORT}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['ndfErp'],
                            searchableName: 'menu.sunsystems-erp',
                            permissions: ['READ.NDF'],
                            requiredFeature: FeatureFlag.NDF_EXPORT,
                        },
                        {
                            path: '/reports-open-advances',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<OpenAdvancesForm />}
                                    permissions={['READ.ACCOUNTING_REPORTS']}
                                    requiredFeature={FeatureFlag.ACCOUNTING_REPORTS}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['openAdvances'],
                            searchableName: 'reports.open-advances.title',
                            permissions: ['READ.ACCOUNTING_REPORTS'],
                            requiredFeature: FeatureFlag.ACCOUNTING_REPORTS,
                        },
                        {
                            path: '/reports-open-invoices',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<OpenInvoicesForm />}
                                    permissions={['READ.ACCOUNTING_REPORTS']}
                                    requiredFeature={FeatureFlag.ACCOUNTING_REPORTS}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['openInvoices'],
                            searchableName: 'menu.open-invoices',
                            permissions: ['READ.ACCOUNTING_REPORTS'],
                            requiredFeature: FeatureFlag.ACCOUNTING_REPORTS,
                        },
                        {
                            path: '/reports-unpaid-invoices',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<UnpaidInvoicesForm />}
                                    permissions={['READ.ACCOUNTING_REPORTS']}
                                    requiredFeature={FeatureFlag.ACCOUNTING_REPORTS}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['unpaidInvoices'],
                            searchableName: 'menu.unpaid-invoices',
                            permissions: ['READ.ACCOUNTING_REPORTS'],
                            requiredFeature: FeatureFlag.ACCOUNTING_REPORTS,
                        },
                        {
                            path: '/reports-sales-journal',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<SalesJournalForm />}
                                    permissions={['READ.ACCOUNTING_REPORTS']}
                                    requiredFeature={FeatureFlag.ACCOUNTING_REPORTS}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['salesJournal'],
                            searchableName: 'reports.sales-journal.title',
                            permissions: ['READ.ACCOUNTING_REPORTS'],
                            requiredFeature: FeatureFlag.ACCOUNTING_REPORTS,
                        },
                        {
                            path: '/reports-foreign-sales',
                            element: (
                                <ProtectedRoute
                                    redirectPath="/"
                                    element={<ForeignSalesForm />}
                                    permissions={['READ.ACCOUNTING_REPORTS']}
                                    requiredFeature={FeatureFlag.ACCOUNTING_REPORTS}
                                />
                            ),
                            breadcrumbs: BREADCRUMB_COLLECTION['foreignSales'],
                            searchableName: 'reports.foreign-sales.title',
                            permissions: ['READ.ACCOUNTING_REPORTS'],
                            requiredFeature: FeatureFlag.ACCOUNTING_REPORTS,
                        },
                    ],
                },
            ],
        },
    ],
};

export default MainRoutes;
